import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { SITE_URL } from "@/lib/siteConfig";

const TITLE = "Clinical Advisory Board | Myelina Health";
const DESC =
  "The neurologists, MS nurses, and lived-experience advisors who pressure-test how Myelina behaves before it ships to real MS days.";

type Advisor = {
  name: string;
  role: string;
  bio: string;
  focus: string;
  placeholder?: boolean;
};

const advisors: Advisor[] = [
  {
    name: "Seat open | MS Neurologist",
    role: "Clinical lead, disease-modifying therapy interactions",
    bio: "We're recruiting an academic MS neurologist to review our fatigue framing and DMT-related copy quarterly.",
    focus: "Safety of medical framing, DMT interactions, red-flag routing",
    placeholder: true,
  },
  {
    name: "Seat open | MS Nurse Practitioner",
    role: "Day-to-day clinical realism",
    bio: "An MS nurse who sees patients weekly, keeping the product honest about what fits into a 15-minute visit.",
    focus: "Care-team share format, clinician onboarding, plain-language rewrites",
    placeholder: true,
  },
  {
    name: "Seat open. Lived-experience advisor",
    role: "Person living with MS",
    bio: "A person living with MS on our advisory board, paid, to review copy, flows, and edge cases before they ship.",
    focus: "Tone, accessibility, cognitive load, dark-pattern audits",
    placeholder: true,
  },
  {
    name: "Seat open. Rehabilitation / OT",
    role: "Occupational therapist, energy conservation",
    bio: "An OT specializing in energy conservation, to keep our pacing and 'windows to protect' guidance grounded.",
    focus: "Pacing science, activity budgeting, wearable interpretation",
    placeholder: true,
  },
];

const Advisors = () => {
  useEffect(() => {
    document.title = TITLE;
    const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", DESC);
    setMeta("og:title", TITLE, "property");
    setMeta("og:description", DESC, "property");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${SITE_URL}/advisors`);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-6 sm:px-10 lg:px-16 py-24 max-w-4xl">
        <p className="text-[11px] tracking-[0.28em] uppercase text-muted-foreground mb-4">
          Clinical Advisory Board
        </p>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight mb-6">
          People who pressure-test what we ship.
        </h1>
        <p className="lead-italic text-foreground/80 mb-12 text-lg">
          Myelina is a wellness tool, not a medical device, but the words we use land on real MS days.
          Our advisors review the fatigue framing, the care-team share format, and the edge cases before
          anything reaches your dashboard.
        </p>

        <section className="grid gap-5 md:grid-cols-2 mb-16">
          {advisors.map((a) => (
            <article
              key={a.name}
              className="rounded-2xl border border-border/60 p-6 bg-card/50 hover:border-primary/40 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h2 className="font-display text-xl leading-tight">{a.name}</h2>
                {a.placeholder && (
                  <span className="shrink-0 text-[10px] tracking-[0.2em] uppercase text-primary/80 border border-primary/30 rounded-full px-2 py-1">
                    Recruiting
                  </span>
                )}
              </div>
              <p className="text-sm text-primary/80 mb-3">{a.role}</p>
              <p className="text-[15px] text-foreground/80 leading-relaxed mb-4">{a.bio}</p>
              <p className="text-xs text-muted-foreground">
                <span className="uppercase tracking-[0.18em] mr-2">Focus</span>
                {a.focus}
              </p>
            </article>
          ))}
        </section>

        <section className="rounded-2xl border border-primary/20 bg-primary/5 p-8 mb-12">
          <h2 className="font-display text-2xl mb-3">How the board actually works</h2>
          <ul className="space-y-2 text-[15px] text-foreground/85 leading-relaxed list-disc pl-5">
            <li>Quarterly review of any new user-facing copy that touches symptoms, medications, or DMTs.</li>
            <li>Any advisor can flag a screen; flagged screens don't ship until they're re-reviewed.</li>
            <li>Advisors are compensated for their time, this isn't an unpaid endorsement list.</li>
            <li>Advisors do not have access to individual user data. Ever.</li>
            <li>We publish changes prompted by advisor feedback in the <Link to="/changelog" className="underline">changelog</Link>.</li>
          </ul>
        </section>

        <section className="text-center">
          <h2 className="font-display text-2xl mb-3">Interested in joining?</h2>
          <p className="text-foreground/75 mb-6 max-w-xl mx-auto">
            If you're an MS clinician, researcher, or person living with MS who wants to shape how this
            product behaves, we'd love to hear from you.
          </p>
          <a
            href="mailto:advisors@myelinahealth.com?subject=Clinical%20Advisory%20Board"
            className="inline-flex items-center justify-center min-h-[48px] px-6 rounded-xl bg-primary text-primary-foreground font-medium text-[14px] hover:opacity-90 transition-opacity"
          >
            Email advisors@myelinahealth.com
          </a>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Advisors;
